import axios from 'axios';
import { CheckoutSessionRequest, CheckoutSessionResponse } from './types/subscription';
import { logger } from './shared/logger';
import { auth } from './firebase';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { ErrorHandler } from './errors';
import { AvailableTimeSlot, EventCheckoutRequest, EventData, EventParticipant, EventRegistrationStatus, FacilityEvent, FacilityMember, FacilitySetupStatus, GuestBooking, PublicAccessSettings, StripeStatus, UpdateMemberData } from './api_types';

import { Facility, 
  PublicPricingSettings,
  PublicFacility,
  CourtAvailability,
  GuestBookingRequest,
  GuestBookingResponse,
  BusinessUser,
  Court, 
  CreateCourtData,
  SubscriptionPlan,
  ImportSubscriber,
  ImportResult,
  MemberData,
  BusinessAnalytics,
  MemberSubscription,
  MemberRegistrationRequest,
  PPUMemberRegistrationRequest,
  MemberRegistrationResponse,
  CheckoutCompletionResponse,
  StripeAccountInfo,
  StringingOrder,
  StringingOrderCreate,
  Class,
  ClassRegistration,
  BusinessHours,
  InviteRequest,
  InviteResponse,
  PaymentMethodListResponse,
  HelloSignAuthResponse,
  HelloSignAccountInfo,
  HelloSignSignatureRequest,
  HelloSignSigningResponse,
  HelloSignDocumentResponse,
  HelloSignSignatureRequestList,
  DocumentDeleteResponse,
  Document,
  SignDocumentRequest,
  SignDocumentResponse,
  ReservationResponse,
  StringingCheckoutResponse,
  RecurringEvent,
  InviteCode,
  UpcomingReservation
} from './api_types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance with default config
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
});

// Add request interceptor for authentication
api.interceptors.request.use(
  async (config) => {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response: any) => {
    // Log successful responses for debugging
    logger.debug('API Response:', response);
    return response;
  },
  (error: any) => {
    const apiError = ErrorHandler.handle(error);
    logger.error('API Error:', apiError);
    return Promise.reject(apiError);
  }
);

const handleApiError = (error: any) => {
  const apiError = ErrorHandler.handle(error);
  console.error('API Error:', apiError);
  throw apiError;
};

export const facilityApi = {
  getFacility: async (facilityId: string, businessId: string): Promise<Facility> => {
    const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}`);
    return response.data;
  },

  getFacilityMembers: async (businessId: string, facilityId: string): Promise<FacilityMember[]> => {
    const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/members`);
    return response.data;
  },

  updateFacility: async (facilityId: string, data: Omit<Facility, 'id' | 'created_at' | 'updated_at'>, businessId: string): Promise<Facility> => {
    const response = await api.put(`/api/businesses/${businessId}/facilities/${facilityId}`, {
      ...data,
      isPPU: data.isPPU || false
    });
    return response.data as Facility;
  },

  getFacilityCompleteStatus: async (facilityId: string, businessId: string): Promise<FacilityCompleteStatus> => {
    const response = await api.get(`/api/facilities/${facilityId}/complete-status`, {
      params: { businessId }
    });
    return response.data;
  },

  getFacilityBusinessHours: async (businessId: string, facilityId: string): Promise<BusinessHours> => {
    const db = getFirestore();
    const facilityRef = doc(db, 'businesses', businessId, 'facilities', facilityId);
    const facilityDoc = await getDoc(facilityRef);
    
    if (!facilityDoc.exists()) {
      throw new Error('Facility not found');
    }
    
    const facilityData = facilityDoc.data();
    const storedHours = facilityData.business_hours || {};
    
    // Initialize all days as closed by default
    const convertedHours: BusinessHours = {
      sunday: { isClosed: true, openTime: '', closeTime: '' },
      monday: { isClosed: true, openTime: '', closeTime: '' },
      tuesday: { isClosed: true, openTime: '', closeTime: '' },
      wednesday: { isClosed: true, openTime: '', closeTime: '' },
      thursday: { isClosed: true, openTime: '', closeTime: '' },
      friday: { isClosed: true, openTime: '', closeTime: '' },
      saturday: { isClosed: true, openTime: '', closeTime: '' }
    };

    // Convert stored hours to new format
    Object.entries(storedHours).forEach(([day, hours]) => {
      if (typeof hours === 'string') {
        if (hours === 'Closed') {
          convertedHours[day as keyof BusinessHours] = { isClosed: true, openTime: '', closeTime: '' };
        } else {
          const [openTime, closeTime] = hours.split(' - ').map(t => formatTimeForInput(t));
          convertedHours[day as keyof BusinessHours] = { isClosed: false, openTime, closeTime };
        }
      }
    });

    return convertedHours;
  },
};

// Business User API
export const businessUserApi = {
  getBusinessUsers: async (businessId?: string): Promise<BusinessUser[]> => {
    const response = await api.get(`/api/businesses/${businessId}/users`);
    return response.data as BusinessUser[];
  },
  deleteBusinessUser: async (userId: string): Promise<void> => {
    await api.delete(`/api/business-users/${userId}`);
  },
};

// Court API
export const courtApi = {
  createCourt: async (businessId: string, facilityId: string, data: CreateCourtData): Promise<Court> => {
    const response = await api.post(`/api/businesses/${businessId}/facilities/${facilityId}/courts`, data);
    return response.data;
  },

  updateCourt: async (businessId: string, facilityId: string, courtId: string, data: Partial<CreateCourtData>): Promise<Court> => {
    const response = await api.put(`/api/businesses/${businessId}/facilities/${facilityId}/courts/${courtId}`, data);
    return response.data;
  },

  getCourts: async (businessId: string, facilityId: string): Promise<Court[]> => {
    const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/courts`);
    return response.data;
  },

  deleteCourt: async (businessId: string, facilityId: string, courtId: string): Promise<void> => {
    await api.delete(`/api/businesses/${businessId}/facilities/${facilityId}/courts/${courtId}`);
  },

  updateReservation: async (reservationId: string, reservation: any): Promise<ReservationResponse> => {
    const response = await fetch(`${API_BASE_URL}/reservations/${reservationId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reservation),
    });
    if (!response.ok) {
      throw new Error('Failed to update reservation');
    }
    return response.json();
  },
};

// Stripe Connect API
export const stripeConnectApi = {
  getFacilityStripeStatus: async (businessId: string, facilityId: string): Promise<StripeStatus> => {
    const response = await api.get(`/connect/businesses/${businessId}/facilities/${facilityId}/stripe-status`);
    return response.data;
  },
  
  onboardBusiness: async (businessId: string, facilityId: string): Promise<{ accountId: string; message?: string }> => {
    const response = await api.post('/connect/onboard', { businessId, facilityId });
    return response.data;
  },

  createAccountLink: async (businessId: string, facilityId: string): Promise<{ url: string }> => {
    const response = await api.post('/connect/account-link', { businessId, facilityId });
    return response.data;
  },

  createPlan: async (businessId: string, facilityId: string, planName: string, interval: 'month' | 'year', priceInCents: number, initiationFeeInCents: number = 0): Promise<{
    planId: string;
    productId: string;
    priceId: string;
    name: string;
    interval: string;
    amount: number;
    initiationFee: number;
  }> => {
    const response = await api.post('/connect/create-plan', {
      businessId,
      facilityId,
      planName,
      interval,
      priceInCents,
      initiationFeeInCents
    });
    return response.data;
  },

  importSubscriptions: async (businessId: string, facilityId: string, priceId: string, subscribers: ImportSubscriber[]): Promise<{
    results: ImportResult[];
  }> => {
    const response = await api.post('/connect/import-subscriptions', {
      businessId,
      facilityId,
      priceId,
      subscribers
    });
    return response.data;
  },

  getPlans: async (businessId: string, facilityId: string): Promise<{
    plans: SubscriptionPlan[];
  }> => {
    const response = await api.get(`/connect/plans?businessId=${businessId}&facilityId=${facilityId}`);
    return response.data;
  },

  updatePublicPricingSettings: async (businessId: string, facilityId: string, settings: PublicPricingSettings): Promise<void> => {
    const response = await api.put(`/api/guest/public-pricing/${businessId}/facilities/${facilityId}`, settings);
    return response.data;
  },
};

// Subscription API
export const subscriptionApi = {
  getSubscriptionPlans: async (businessId: string, facilityId: string): Promise<SubscriptionPlan[]> => {
    const response = await api.get(`/connect/plans?businessId=${businessId}&facilityId=${facilityId}`);
    return response.data.plans;
  },

  archiveSubscriptionPlan: async (planId: string, facilityId: string, businessId: string): Promise<void> => {
    await api.post(`/businesses/${businessId}/facilities/${facilityId}/plans/${planId}/archive`);
  },

  createCheckoutSession: async (request: CheckoutSessionRequest): Promise<CheckoutSessionResponse> => {
    const response = await api.post('/connect/checkout/session', request);
    return response.data;
  },
};

export interface Invoice {
  id: string;
  amount: number;
  status: string;
  date: string;
  pdfUrl: string;
  hostedUrl: string;
}

export const businessAnalyticsApi = {
  getSubscriptionAnalytics: async (businessId: string, facilityId: string, token: string): Promise<BusinessAnalytics> => {
    const response = await api.get(`/api/business/${businessId}/facilities/${facilityId}/subscription-analytics`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  },

  getMemberInvoices: async (memberId: string, token: string, businessId: string, facilityId: string): Promise<Invoice[]> => {
    const response = await api.get(`/api/business/${businessId}/facilities/${facilityId}/members/${memberId}/invoices`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  }
};

export const memberApi = {

  updateMemberData: async (memberId: string, data: UpdateMemberData, businessId: string, facilityId: string): Promise<MemberData> => {
    const response = await api.put(`/api/businesses/${businessId}/facilities/${facilityId}/members/${memberId}`, data);
    return response.data;
  },
  
  async deleteMember(memberId: string, businessId: string, facilityId: string) {
    try {
      const response = await api.delete(`/api/businesses/${businessId}/facilities/${facilityId}/members/${memberId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting member:', error);
      throw error;
    }
  },

  register: async (data: MemberRegistrationRequest): Promise<MemberRegistrationResponse> => {
    const response = await api.post('/api/members/register-with-auth', data);
    return response.data;
  },

  registerPPU: async (data: PPUMemberRegistrationRequest): Promise<MemberRegistrationResponse> => {
    const response = await api.post('/api/members/register-ppu', data);
    return response.data;
  },

  getSubscription: async (memberId: string, token: string, businessId: string, facilityId: string): Promise<MemberSubscription> => {
    const response = await api.get(`/api/members/${memberId}/subscription?business_id=${businessId}&facility_id=${facilityId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  },

  getMemberInvoice: async (memberId: string, token: string, businessId: string, facilityId: string): Promise<Invoice[]> => {
    const response = await api.get(`/api/business/${businessId}/facilities/${facilityId}/members/${memberId}/invoices`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  },

  // Payment Methods
  getPaymentMethods: async (memberId: string, token: string, businessId: string, facilityId: string): Promise<PaymentMethodListResponse> => {
    const response = await api.get(`/connect/payment-methods?member_id=${memberId}&business_id=${businessId}&facility_id=${facilityId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  },

  setDefaultPaymentMethod: async (memberId: string, paymentMethodId: string, token: string, businessId: string, facilityId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.get(`/api/members/${memberId}/subscription`, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      params: {
        business_id: businessId,
        facility_id: facilityId
      }
    });
    const subscription = response.data;
    await api.post('/connect/update-subscription-payment', {
      member_id: memberId,
      payment_method_id: paymentMethodId,
      subscription_id: subscription.id
    }, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      params: {
        business_id: businessId,
        facility_id: facilityId
      }
    });
    return {
      success: true,
      message: 'Payment method set as default successfully'
    };
  },

  detachPaymentMethod: async (memberId: string, paymentMethodId: string, token: string, businessId: string, facilityId: string): Promise<void> => {
    await api.post(`/connect/payment-methods/detach?business_id=${businessId}&facility_id=${facilityId}`, {
      member_id: memberId,
      payment_method_id: paymentMethodId
    }, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  createSetupIntent: async (memberId: string, token: string, businessId: string, facilityId: string): Promise<{ url: string }> => {
    const response = await api.post(`/connect/setup-intent?member_id=${memberId}&business_id=${businessId}&facility_id=${facilityId}`, null, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  },

  getFacilityMembers: async (businessId: string, facilityId: string): Promise<FacilityMember[]> => {
    const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/users`);
    return response.data;
  },

  getMemberDetails: async (businessId: string, memberId: string, facilityId: string): Promise<{ firstName: string; lastName: string }> => {
    const response = await api.get(`/api/businesses/${businessId}/members/${memberId}/details?facility_id=${facilityId}`);
    return response.data;
  },

  // Member Dashboard
  getMemberData: async (memberId: string, businessId: string, facilityId: string): Promise<MemberData> => {
    const response = await api.get(`/api/members/${memberId}?business_id=${businessId}&facility_id=${facilityId}`);
    return response.data;
  },

  getMemberReservations: async (memberId: string, businessId: string, facilityId: string): Promise<UpcomingReservation[]> => {
    const response = await api.get(`/api/members/${memberId}/reservations?business_id=${businessId}&facility_id=${facilityId}`);
    return response.data;
  },

  getAvailableSlots: async (
    memberId: string,
    businessId: string,
    duration: string = "1hour",
    preferredWindow: number = 3,
    facilityId: string
  ): Promise<AvailableTimeSlot[]> => {
    const response = await api.get(`/api/members/${memberId}/available-slots`, {
      params: { business_id: businessId, duration, preferred_window: preferredWindow, facility_id: facilityId }
    });
    return response.data;
  },

  getMemberEvents: async (memberId: string, businessId: string, facilityId: string): Promise<EventData[]> => {
    const response = await api.get(`/api/members/${memberId}/events?business_id=${businessId}&facility_id=${facilityId}`);
    return response.data;
  },
};

export const completeCheckout = async (sessionId: string, businessId: string, facilityId: string): Promise<CheckoutCompletionResponse> => {
  try {
    const response = await api.post('/connect/checkout/complete', {
      sessionId,
      businessId,
      facilityId
    });
    return response.data;
  } catch (error: any) {
    // Log the full error response for debugging
    logger.error('Checkout completion failed:', {
      error: error.error?.message || error.message,
      details: error.error?.details,
      code: error.error?.code
    });
    throw error;
  }
};

// Business API functions
export const businessApi = {
    getStripeAccountInfo: async (businessId: string, facilityId: string): Promise<{ data: StripeAccountInfo }> => {
        try {
            const response = await api.get(`/business/stripe/account?businessId=${businessId}&facilityId=${facilityId}`);
            return response.data;
        } catch (error) {
            handleApiError(error);
            throw error;
        }
    },
};

// PPU API
export const ppuApi = {
  getPPUFacilities: async (): Promise<Facility[]> => {
    const response = await api.get('/api/ppu/facilities');
    return response.data as Facility[];
  },

  createBooking: async (bookingData: any) => {
    const response = await api.post('/api/ppu/bookings', bookingData);
    return response.data;
  },
  getBookingStatus: async (bookingId: string, businessId: string, facilityId: string) => {
    const response = await api.get(`/api/ppu/bookings/${businessId}/${facilityId}/${bookingId}/status`);
    return response.data;
  }
};

// Guest API
export const guestApi = {
  getPublicPricingSettings: async (businessId: string, facilityId: string): Promise<PublicPricingSettings> => {
    const response = await api.get(`/api/guest/businesses/${businessId}/facilities/${facilityId}/public-pricing`);
    return response.data;
  },

  getPublicFacilities: async (): Promise<PublicFacility[]> => {
    try {
      const response = await api.get('/api/guest/facilities');
      return response.data;
    } catch (error) {
      console.error('Error fetching public facilities:', error);
      throw error;
    }
  },

  getFacilityDetails: async (businessId: string, facilityId: string): Promise<PublicFacility> => {
    try {
      const response = await api.get(`/api/guest/facilities/${businessId}/${facilityId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching facility details:', error);
      throw error;
    }
  },

  createBooking: async (bookingData: GuestBookingRequest): Promise<GuestBookingResponse> => {
    try {
      const response = await api.post(`${API_BASE_URL}/api/guest/bookings`, bookingData);
      return response.data;
    } catch (error) {
      console.error('Error creating booking:', error);
      throw error;
    }
  },

  confirmBooking: async (
    bookingId: string,
    sessionId: string,
    businessId: string,
    facilityId: string
  ): Promise<{ status: string; bookingId: string }> => {
    try {
      const response = await api.post(`${API_BASE_URL}/connect/checkout/confirm`, {
        bookingId,
        sessionId,
        businessId,
        facilityId
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.error?.message || error.message || 'Failed to confirm booking payment'
      );
    }
  },

  verifyBookingStatus: async (bookingId: string, businessId: string, facilityId: string): Promise<GuestBookingResponse> => {
    try {
      const response = await api.get(`${API_BASE_URL}/api/guest/bookings/${bookingId}/status/${businessId}/${facilityId}`);
      return response.data;
    } catch (error) {
      console.error('Error verifying booking status:', error);
      throw error;
    }
  },

  sendBookingConfirmationEmail: async (bookingId: string, businessId: string, facilityId: string): Promise<void> => {
    try {
      await api.post(`/api/guest/bookings/${bookingId}/send-confirmation`, null, {
        params: {
          business_id: businessId,
          facility_id: facilityId
        }
      });
    } catch (error) {
      console.error('Error sending confirmation email:', error);
      throw error;
    }
  },

  getPublicAccessSettings: async (businessId: string, facilityId: string): Promise<PublicAccessSettings> => {
    const response = await api.get(`/api/guest/businesses/${businessId}/facilities/${facilityId}/public-access`);
    return response.data;
  },

  updatePublicAccessSettings: async (businessId: string, facilityId: string, settings: { openToPublic: boolean }): Promise<{ message: string }> => {
    const response = await api.put(`/api/guest/businesses/${businessId}/facilities/${facilityId}/public-access`, settings);
    return response.data;
  },

  getGuestBookings: async (businessId: string, facilityId: string): Promise<GuestBooking[]> => {
    const response = await api.get(`/api/guest/businesses/${businessId}/facilities/${facilityId}/guest-bookings`);
    return response.data;
  },
};

export interface StaffUser {
  id: string;
  name: string;
  email: string;
  phone_number: string;
  role: string;
  facility_id: string;
  created_at: string;
  updated_at: string;
}

export interface StaffUserRegistration {
  name: string;
  email: string;
  phone_number: string;
  role: string;
  facility_id: string;
  password: string;
}

// Staff API
export const staffApi = {
  registerStaffUser: async (data: StaffUserRegistration, businessId: string, facilityId: string): Promise<StaffUser> => {
    const response = await api.post('/api/staff/register', data, {
      params: { business_id: businessId, facility_id: facilityId }
    });
    return response.data as StaffUser;
  },

  getStaffUsers: async (businessId: string): Promise<StaffUser[]> => {
    const response = await api.get('/api/staff', {
      params: { business_id: businessId }
    });
    return response.data as StaffUser[];
  },

  getStaffUser: async (staffId: string, businessId: string): Promise<StaffUser> => {
    const response = await api.get(`/api/staff/${staffId}`, {
      params: { business_id: businessId }
    });
    return response.data as StaffUser;
  },

  // Unused at the moment
  updateStaffUser: async (staffId: string, data: Partial<StaffUserRegistration>, facilityId: string): Promise<StaffUser> => {
    const response = await api.put(`/api/staff/${staffId}`, data, {
      params: { facility_id: facilityId }
    });
    return response.data as StaffUser;
  },

  deleteStaffUser: async (staffId: string, facilityId: string): Promise<void> => {
    await api.delete(`/api/staff/${staffId}`, {
      params: { facility_id: facilityId }
    });
  }
};

// Add new interface for occurrences
export interface RecurringEventOccurrence {
  id: string;
  parentEventId: string;
  date: string;
  startTime: string;
  endTime: string;
  title: string;
  description: string;
  courtIds: string[];
  capacity?: number;
  registrationRequired: boolean;
  registrationDeadline?: string;
  price?: number;
  status: 'scheduled' | 'cancelled' | 'completed';
  isException: boolean;
}

export const eventApi = {
  getFacilityEvents: async (businessId: string, facilityId: string): Promise<EventData[]> => {
    const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/events`);
    return response.data;
  },
  createEvent: async (businessId: string, facilityId: string, eventData: EventData): Promise<{ id: string; message: string }> => {
    const response = await api.post(`/api/businesses/${businessId}/facilities/${facilityId}/events`, eventData);
    return response.data;
  },
  updateEvent: async (businessId: string, facilityId: string, eventId: string, eventData: EventData): Promise<{ message: string }> => {
    const response = await api.put(`/api/businesses/${businessId}/facilities/${facilityId}/events/${eventId}`, eventData);
    return response.data;
  },
  deleteEvent: async (businessId: string, facilityId: string, eventId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/api/businesses/${businessId}/facilities/${facilityId}/events/${eventId}`);
    return response.data;
  },
  getFacilityEventsForDate: async (businessId: string, facilityId: string, date: string): Promise<FacilityEvent[]> => {
    const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/events`, {
      params: { date }
    });
    return response.data;
  },

  getFacilityRecurringEvents: async (businessId: string, facilityId: string): Promise<RecurringEvent[]> => {
    const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/recurring-events`);
    return response.data;
  },

  addEventParticipant: async (businessId: string, facilityId: string, eventId: string, participant: EventParticipant): Promise<{ id: string; message: string }> => {
    const response = await api.post(`/api/businesses/${businessId}/facilities/${facilityId}/events/${eventId}/participants`, participant, {
    });
    return response.data;
  },

  removeEventParticipant: async (businessId: string, facilityId: string, eventId: string, participantId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/api/businesses/${businessId}/facilities/${facilityId}/events/${eventId}/participants/${participantId}`);
    return response.data;
  },

  addRecurringEventParticipant: async (businessId: string, facilityId: string, eventId: string, participant: EventParticipant): Promise<{ id: string; message: string }> => {
    const response = await api.post(`/api/businesses/${businessId}/facilities/${facilityId}/recurring-events/${eventId}/participants`, participant);
    return response.data;
  },

  // Event Checkout
  async createEventCheckout(businessId: string, facilityId: string, eventId: string, request: EventCheckoutRequest) {
    try {
      const response = await api.post(`/api/businesses/${businessId}/facilities/${facilityId}/events/${eventId}/checkout`, request);

      if (response.status !== 200) {
        throw new Error('Failed to create checkout session');
      }

      const data = response.data;
      return {
        url: data.url,
        sessionId: data.sessionId,
        creditsUsed: data.creditsUsed || 0,
        newBalance: data.newBalance || 0
      };
    } catch (error) {
      logger.error('Error creating event checkout:', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  },

  async getEventRegistrationStatus(eventId: string, sessionId: string): Promise<EventRegistrationStatus> {
    try {
      const response = await api.get(`/api/events/${eventId}/registration-status?session_id=${sessionId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status !== 200) {
        throw new Error('Failed to get registration status');
      }

      return response.data;
    } catch (error) {
      logger.error('Error getting event registration status:', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  },
}

// Recurring Events API
export const recurringEventApi = {
  async createRecurringEvent(businessId: string, facilityId: string, eventData: Omit<RecurringEvent, 'id' | 'createdAt' | 'updatedAt'>): Promise<RecurringEvent> {
    const response = await fetch(`${API_BASE_URL}/api/businesses/${businessId}/facilities/${facilityId}/recurring-events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getAuthToken()}`,
      },
      body: JSON.stringify(eventData),
    });

    if (!response.ok) {
      throw new Error('Failed to create recurring event');
    }

    return response.json();
  },

  async updateRecurringEvent(businessId: string, facilityId: string, eventId: string, eventData: Partial<RecurringEvent>): Promise<RecurringEvent> {
    const response = await fetch(`${API_BASE_URL}/api/businesses/${businessId}/facilities/${facilityId}/recurring-events/${eventId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getAuthToken()}`,
      },
      body: JSON.stringify(eventData),
    });

    if (!response.ok) {
      throw new Error('Failed to update recurring event');
    }

    return response.json();
  },

  async getRecurringEvents(businessId: string, facilityId: string): Promise<RecurringEvent[]> {
    const response = await fetch(`${API_BASE_URL}/api/businesses/${businessId}/facilities/${facilityId}/recurring-events`, {
      headers: {
        'Authorization': `Bearer ${await getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch recurring events');
    }

    return response.json();
  },

  async getRecurringEventOccurrencesByDate(businessId: string, facilityId: string, date: string): Promise<RecurringEventOccurrence[]> {
    const response = await fetch(`${API_BASE_URL}/api/businesses/${businessId}/facilities/${facilityId}/recurring-events/date/${date}`, {
      headers: {
        'Authorization': `Bearer ${await getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch recurring event occurrences');
    }

    return response.json();
  },

  async updateRecurringEventOccurrence(
    businessId: string,
    facilityId: string, 
    eventId: string, 
    occurrenceId: string, 
    data: Partial<RecurringEventOccurrence>
  ): Promise<RecurringEventOccurrence> {
    const response = await fetch(`${API_BASE_URL}/api/businesses/${businessId}/facilities/${facilityId}/recurring-events/${eventId}/occurrences/${occurrenceId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getAuthToken()}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update recurring event occurrence');
    }

    return response.json();
  },

  async cancelRecurringEventOccurrence(businessId: string, facilityId: string, eventId: string, occurrenceId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/businesses/${businessId}/facilities/${facilityId}/recurring-events/${eventId}/occurrences/${occurrenceId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${await getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to cancel recurring event occurrence');
    }
  },

  async deleteRecurringEvent(businessId: string, facilityId: string, eventId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/businesses/${businessId}/facilities/${facilityId}/recurring-events/${eventId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${await getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to delete recurring event');
    }
  },

  batchCheckRecurringConflicts: async (businessId: string, facilityId: string, params: {
      courtIds: string[];
      dates: string[];
      startTime: string;
      endTime: string;
      excludeRecurringEventId?: string;
    }): Promise<{ hasConflict: boolean; conflictMessage?: string; conflictDate?: string }> => {
      const response = await api.post(`/api/businesses/${businessId}/facilities/${facilityId}/batch-check-recurring-conflicts`, params);
      return response.data;
    },

    removeRecurringEventParticipant: async (businessId: string, facilityId: string, eventId: string, participantId: string): Promise<{ message: string }> => {
      const response = await api.delete(`/api/businesses/${businessId}/facilities/${facilityId}/recurring-events/${eventId}/participants/${participantId}`);
      return response.data;
    },
};

const getAuthToken = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.getIdToken();
};

export const createClass = async (businessId: string, facilityId: string, classData: Omit<Class, 'id' | 'createdAt' | 'updatedAt' | 'registeredMembers'>): Promise<Class> => {
  try {
    const response = await api.post(`/api/businesses/${businessId}/facilities/${facilityId}/classes`, classData);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getFacilityClasses = async (businessId: string, facilityId: string): Promise<Class[]> => {
  const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/classes`);
  return response.data;
};

export const updateClass = async (businessId: string, facilityId: string, classId: string, classData: Partial<Class>): Promise<Class> => {
  try {
    const response = await api.put(`/api/businesses/${businessId}/facilities/${facilityId}/classes/${classId}`, classData);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const deleteClass = async (businessId: string, facilityId: string, classId: string): Promise<void> => {
  try {
    await api.delete(`/api/businesses/${businessId}/facilities/${facilityId}/classes/${classId}`);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getClassRegistrations = async (businessId: string, facilityId: string, classId: string): Promise<ClassRegistration[]> => {
  const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/classes/${classId}/registrations`);
  return response.data;
};

const formatTimeForInput = (time: string): string => {
  // Convert time from "HH:MM AM/PM" to "HH:MM" format
  const [timePart, period] = time.split(' ');
  const [hours, minutes] = timePart.split(':');
  let hour = parseInt(hours);
  
  if (period === 'PM' && hour !== 12) {
    hour += 12;
  } else if (period === 'AM' && hour === 12) {
    hour = 0;
  }
  
  return `${hour.toString().padStart(2, '0')}:${minutes}`;
};

export const getFacilityBusinessHours = async (businessId: string, facilityId: string): Promise<BusinessHours> => {
  try {
    const db = getFirestore();
    const facilityRef = doc(db, 'businesses', businessId, 'facilities', facilityId);
    const facilityDoc = await getDoc(facilityRef);
    
    if (!facilityDoc.exists()) {
      throw new Error('Facility not found');
    }
    
    const facilityData = facilityDoc.data();
    const storedHours = facilityData.business_hours || {};
    
    // Initialize all days as closed by default
    const convertedHours: BusinessHours = {
      sunday: { isClosed: true, openTime: '', closeTime: '' },
      monday: { isClosed: true, openTime: '', closeTime: '' },
      tuesday: { isClosed: true, openTime: '', closeTime: '' },
      wednesday: { isClosed: true, openTime: '', closeTime: '' },
      thursday: { isClosed: true, openTime: '', closeTime: '' },
      friday: { isClosed: true, openTime: '', closeTime: '' },
      saturday: { isClosed: true, openTime: '', closeTime: '' }
    };

    // Convert stored hours to new format
    Object.entries(storedHours).forEach(([day, hours]) => {
      if (typeof hours === 'string') {
        if (hours === 'Closed') {
          convertedHours[day as keyof BusinessHours] = { isClosed: true, openTime: '', closeTime: '' };
        } else {
          const [openTime, closeTime] = hours.split(' - ').map(t => formatTimeForInput(t));
          convertedHours[day as keyof BusinessHours] = { isClosed: false, openTime, closeTime };
        }
      }
    });

    return convertedHours;
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to fetch facility business hours');
    logger.error('Error fetching facility business hours:', error);
    throw error;
  }
};

export const classApi = {
  getFacilityClasses: async (businessId: string, facilityId: string): Promise<Class[]> => {
    const response = await fetch(`${API_BASE_URL}/api/businesses/${businessId}/facilities/${facilityId}/classes`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error('Failed to fetch facility classes');
    }
    return response.json();
  },

  createClassCheckout: async (
    businessId: string,
    facilityId: string,
    classId: string,
    memberId: string,
    successUrl: string,
    cancelUrl: string,
    useCredits: boolean = false,
    creditAmount: number = 0
  ): Promise<{ sessionId: string; url: string }> => {
    const response = await api.post(`/api/businesses/${businessId}/facilities/${facilityId}/classes/${classId}/checkout`, {
        classId,
        memberId,
        successUrl,
        cancelUrl,
        useCredits,
        creditAmount
      });
    return response.data;
  },

  checkRegistrationStatus: async (
    businessId: string,
    facilityId: string,
    classId: string,
    sessionId: string
  ): Promise<{ status: string; paymentStatus: string }> => {
    const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/classes/${classId}/registration-status?session_id=${sessionId}`);
    return response.data;
  },

  refundClassRegistration: async (
    businessId: string,
    facilityId: string,
    classId: string,
    registrationId: string
  ): Promise<{ message: string; refundAmount: number; newBalance: number }> => {
    const response = await api.post(`/api/businesses/${businessId}/facilities/${facilityId}/classes/${classId}/refund`, {
      registration_id: registrationId
    });
    return response.data;
  },

  getClassRegistrations: async (businessId: string, facilityId: string, classId: string): Promise<ClassRegistration[]> => {
    const response = await fetch(
      `${API_BASE_URL}/api/businesses/${businessId}/facilities/${facilityId}/classes/${classId}/registrations`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch class registrations');
    }
    return response.json();
  },

  getMemberCredits: async (memberId: string, businessId: string, facilityId: string): Promise<{ balance: number }> => {
    const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/members/${memberId}/credits`);
    return response.data;
  },

  registerWithCredits: async (businessId: string, facilityId: string, classId: string, memberId: string): Promise<any> => {
    const response = await api.post(`/api/businesses/${businessId}/facilities/${facilityId}/classes/${classId}/register-with-credits?member_id=${memberId}`);
    return response.data;
  },
};

// Marketing Invite API
export const inviteApi = {
  sendInvite: async (data: InviteRequest): Promise<InviteResponse> => {
    try {
      const response = await api.post('/api/invite/send', data);
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  sendMigrationSetupEmails: async (businessId: string, facilityId: string): Promise<{ success: boolean; message: string; count: number }> => {
    try {
      const response = await api.post(`/api/migrate/send-setup-emails/${businessId}/facilities/${facilityId}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  // Invite code methods
  getInviteCodes: async (businessId: string, facilityId: string): Promise<InviteCode[]> => {
    const response = await api.get(`/api/invite-codes/businesses/${businessId}/facilities/${facilityId}/invite-codes`);
    return response.data;
  },

  createInviteCode: async (businessId: string, facilityId: string): Promise<InviteCode> => {
    const response = await api.post(`/api/invite-codes/businesses/${businessId}/facilities/${facilityId}/invite-codes`);
    return response.data;
  },

  deleteInviteCode: async (businessId: string, facilityId: string, codeId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/api/invite-codes/businesses/${businessId}/facilities/${facilityId}/invite-codes/${codeId}`);
    return response.data;
  },

  getFacilitySetupStatus: async (businessId: string, facilityId: string): Promise<FacilitySetupStatus> => {
    const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/setup-status`);
    return response.data;
  },
};

export const aiApi = {
  generateEmail: async (eventDetails: string): Promise<{ subject: string; content: string }> => {
    const response = await api.post('/api/llm/generate-email', { event_details: eventDetails });
    return response.data;
  }
};

// HelloSign API
export const hellosignApi = {
  testConnection: async (): Promise<HelloSignAuthResponse> => {
    const response = await api.get('/api/hellosign/test-connection');
    return response.data;
  },

  getAccountInfo: async (): Promise<HelloSignAccountInfo> => {
    const response = await api.get('/api/hellosign/account-info');
    return response.data;
  },
  
  uploadAndSignDocument: async (file: File, signerEmail: string, signerName: string, title: string, businessId: string): Promise<HelloSignSignatureRequest> => {
    // Create FormData object
    const formData = new FormData();
    formData.append('file', file);
    formData.append('signer_email', signerEmail);
    formData.append('signer_name', signerName);
    formData.append('title', title || file.name);
    formData.append('business_id', businessId);
    
    // Submit to upload endpoint
    const response = await api.post('/api/hellosign/upload-and-sign', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  createSignatureRequest: async (data: {
    title: string;
    subject: string;
    message: string;
    signers: Array<{
      email_address: string;
      name: string;
    }>;
    files: string[];
  }): Promise<HelloSignSignatureRequest> => {
    const response = await api.post('/api/hellosign/create-signature-request', data);
    return response.data;
  },

  getSignatureRequest: async (signatureRequestId: string): Promise<HelloSignSignatureRequest> => {
    const response = await api.get(`/api/hellosign/signature-request/${signatureRequestId}`);
    return response.data;
  },

  listSignatureRequests: async (): Promise<HelloSignSignatureRequestList> => {
    const response = await api.get('/api/hellosign/signature-requests');
    return response.data;
  },

  getSigningUrl: async (signatureRequestId: string): Promise<HelloSignSigningResponse> => {
    const response = await api.get(`/api/hellosign/signing-url/${signatureRequestId}`);
    return response.data;
  },

  downloadSignedDocument: async (signatureRequestId: string): Promise<HelloSignDocumentResponse> => {
    const response = await api.get(`/api/hellosign/download/${signatureRequestId}`);
    return response.data;
  },
  
  deleteDocument: async (documentId: string, businessId?: string): Promise<DocumentDeleteResponse> => {
    const response = await api.delete(`/api/documents/${documentId}`);
    return response.data;
  }
};

export const documentApi = {
  fetchBusinessDocuments: async (facilityId: string): Promise<Document[]> => {
    const response = await api.get(`/api/documents/business/${facilityId}`, {
      params: { facility_id: facilityId }
    });
    return response.data;
  },

  signDocument: async (data: SignDocumentRequest): Promise<SignDocumentResponse> => {
    const response = await api.post('/api/documents/sign', data);
    return response.data;
  },
  
  getHellosignPreviewUrl: (signatureRequestId: string): string => {
    // Return direct URL to the preview endpoint
    return `/api/documents/hellosign/${signatureRequestId}/preview`;
  }
};

// Event Registration API
export const eventRegistrationApi = {
  registerWithCredits: async (businessId: string, facilityId: string, eventId: string, memberId: string) => {
    const response = await api.post(
      `/api/businesses/${businessId}/facilities/${facilityId}/events/${eventId}/register-with-credits?member_id=${memberId}`
    );
    return response.data;
  },

  refundEventRegistration: async (businessId: string, facilityId: string, eventId: string, participantId: string): Promise<{
    message: string;
    refundAmount: number;
    newBalance: number;
  }> => {
    const response = await api.post(`/api/businesses/${businessId}/facilities/${facilityId}/events/${eventId}/refund?participant_id=${participantId}`);
    return response.data;
  },

  getMemberCredits: async (memberId: string, businessId: string, facilityId: string): Promise<{
    balance: number;
  }> => {
    const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/members/${memberId}/credits`);
    return response.data;
  }
};
// Stringing Service API
export const stringingApi = {
  createOrder: async (order: StringingOrderCreate): Promise<StringingOrder> => {
    try {
      const response = await api.post(`/api/businesses/${order.businessId}/facilities/${order.facilityId}/stringing-orders`, order);
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to create stringing order';
      throw new Error(errorMessage);
    }
  },

  getOrders: async (businessId: string, facilityId: string): Promise<StringingOrder[]> => {
    try {
      const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/stringing-orders`);
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to fetch stringing orders';
      throw new Error(errorMessage);
    }
  },

  getOrder: async (businessId: string, facilityId: string, orderId: string): Promise<StringingOrder> => {
    try {
      const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/stringing-orders/${orderId}`);
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to fetch stringing order';
      throw new Error(errorMessage);
    }
  },

  updateOrderStatus: async (businessId: string, facilityId: string, orderId: string, status: string): Promise<void> => {
    try {
      await api.put(`/api/businesses/${businessId}/facilities/${facilityId}/stringing-orders/${orderId}/status`, null, {
        params: { status }
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to update order status';
      throw new Error(errorMessage);
    }
  },

  createCheckout: async (businessId: string, facilityId: string, orderId: string, request: {
    memberId: string;
    useCredits: boolean;
    successUrl: string;
    cancelUrl: string;
  }): Promise<StringingCheckoutResponse> => {
    try {
      const response = await api.post(`/api/businesses/${businessId}/facilities/${facilityId}/stringing-orders/${orderId}/checkout`, request);
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to create checkout session';
      throw new Error(errorMessage);
    }
  },

  getPaymentStatus: async (orderId: string, sessionId: string): Promise<{ status: string; paymentStatus: string; orderId?: string }> => {
    try {
      const response = await api.get(`/api/stringing/${orderId}/payment-status`, {
        params: { session_id: sessionId }
      });
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to get payment status';
      throw new Error(errorMessage);
    }
  },

  getAllOrders: async (businessId: string, facilityId: string): Promise<StringingOrder[]> => {
    try {
      const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/stringing-orders-all`);
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to fetch all stringing orders';
      throw new Error(errorMessage);
    }
  },

  getSavedOrder: async (businessId: string, facilityId: string, userId: string): Promise<{ savedOrder: any }> => {
    try {
      const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/users/${userId}/saved-stringing-order`);
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to fetch saved stringing order';
      throw new Error(errorMessage);
    }
  },
}
// Subscription utility API for migrated users
export const subscriptionUtilsApi = {
  getPlanFromSubscription: async (subscriptionId: string, businessId: string, facilityId: string, token: string): Promise<{
    planId: string | null;
    planName: string;
  }> => {
    const response = await api.get(`/api/members/subscription/${subscriptionId}/plan-info`, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      params: {
        businessId,
        facilityId
      }
    });
    return response.data;
  }
};

export interface Transaction {
  id: string;
  date: string;
  createdAt: string;
  amount: number;
  type: 'subscription' | 'public_use' | 'event_signup' | 'stringing';
  status: string;
  customerName: string;
  description: string;
  eventName?: string;
}

export const transactionsApi = {
  getRecentTransactions: async (businessId: string, facilityId: string, days: number = 7): Promise<Transaction[]> => {
    const response = await api.get(`/api/businesses/${businessId}/facilities/${facilityId}/transactions?days=${days}`);
    return response.data;
  }
};

export interface FacilityCompleteStatus {
  facilityStatus: {
    has_members: boolean;
    has_staff: boolean;
    has_plans: boolean;
    has_public_plans: boolean;
    has_stripe: boolean;
  };
  publicAccessSettings: {
    openToPublic: boolean;
    stripeAccountId?: string;
    updatedAt: string;
  };
  pricingSettings: {
    weekendRate: number;
    weekdayPeakRate: number;
    weekdayOffPeakRate: number;
    peakHours: {
      startTime: string;
      endTime: string;
    };
    updatedAt: string;
  };
  courtCount: number;
}

export interface BusinessInterestForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  businessName: string;
  facilityType: string;
  courtCount: string;
  location: string;
  message?: string;
}

export const submitBusinessInterest = async (formData: BusinessInterestForm): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post('/api/business/interest', formData);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}; 


export const nexusApi = {
  async sendMessage(message: string, businessId: string, facilityId: string): Promise<string> {
    const response = await api.post("/api/nexus", { 
      message,
      businessId,
      facilityId
    });
    return response.data.response;
  }
};